import MapIcon from 'lucide-react-native/icons/map';
import { useEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Radius, Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';
import { stay22MapUrl } from '@/lib/hotels';
import { useLocale } from '@todo/core/i18n/use-locale';

import type { HotelsMapFrameProps, HotelsMapPanelProps, HotelsMapProps } from './hotels-map';

const MAP_HEIGHT = 420;
const WIDE_MAP_HEIGHT = 480;

// detail-desktop.module.css's desktop breakpoint: from here the event page's
// booking panel is a 340px side column.
const DESKTOP_QUERY = '(min-width: 1024px)';

/**
 * The widget itself. It scrolls itself into view when it opens: `wide` (the
 * full-width spot under the event page's main card) is below the fold when the
 * button is pressed, so it's centred; under the button (mobile, city guides,
 * articles) it scrolls only as far as needed, which keeps the button in view.
 * The sandbox lets the widget run and open bookings in a new tab, but never
 * navigate this page.
 */
export function HotelsMapFrame({ src, wide, height, autoScroll = true }: HotelsMapFrameProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const ref = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!autoScroll) return;
    ref.current?.scrollIntoView({ behavior: 'smooth', block: wide ? 'center' : 'nearest' });
  }, [wide, src, autoScroll]);

  return (
    <iframe
      ref={ref}
      src={src}
      title={t('hotels.mapTitle')}
      sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms"
      referrerPolicy="strict-origin-when-cross-origin"
      style={{
        display: 'block',
        width: '100%',
        height: height ?? (wide ? WIDE_MAP_HEIGHT : MAP_HEIGHT),
        border: `1px solid ${theme.border}`,
        borderRadius: Radius.lg,
        boxSizing: 'border-box',
        // A caller that sets the height lays the frame out itself.
        marginTop: height != null ? 0 : wide ? Spacing.four : Spacing.two,
      }}
    />
  );
}

// The /hotels page's map box: as tall as fits a phone screen under the city
// buttons, up to a desktop-sized map.
const PANEL_HEIGHT = 'min(560px, 70vh)';

/**
 * The /hotels page's map — the one exception to HotelsMap's click-to-load
 * rule: the map is what that page is for, so the iframe is in its static HTML
 * and loads with it (owner's call, see AGENTS.md). Every other Stay22 map
 * stays behind a click. `focusKey` scrolls the box into view on every request,
 * including a repeat of the area already shown (the "Show on the map" buttons
 * further down the page).
 */
export function HotelsMapPanel({ src, focusKey }: HotelsMapPanelProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focusKey === 0) return;
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [focusKey]);

  return (
    <div ref={ref} style={{ height: PANEL_HEIGHT, minHeight: 360, scrollMarginTop: Spacing.three }}>
      <HotelsMapFrame src={src} height="100%" autoScroll={false} />
    </div>
  );
}

/**
 * "Show hotels on the map": Stay22's map widget, mounted only once the visitor
 * asks for it. Until then the page has no iframe and loads nothing from
 * Stay22 — the widget is heavy (a full map app) and runs Stay22's own
 * analytics, so neither the page's speed nor its no-tracking default pays for
 * visitors who never open it. The URL is built on open, so its dates are
 * settled against today (see stay22MapUrl).
 *
 * With `wide` (the event page), a desktop visitor's map goes to the page —
 * which renders it full width under its main card — instead of squeezed into
 * the side column under the button. Decided at click time, so the static HTML
 * is the same either way.
 */
export function HotelsMap({ stay, campaign, zoom, wide }: HotelsMapProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [inlineSrc, setInlineSrc] = useState<string | null>(null);
  const isOpen = inlineSrc != null || wide?.src != null;

  const toggle = () => {
    if (isOpen) {
      setInlineSrc(null);
      wide?.setSrc(null);
      return;
    }
    const src = stay22MapUrl(stay, { campaign, locale, zoom });
    if (wide && window.matchMedia(DESKTOP_QUERY).matches) wide.setSrc(src);
    else setInlineSrc(src);
  };

  return (
    <View style={styles.block}>
      <Button
        variant="ghost"
        size="sm"
        style={styles.toggle}
        leadingIcon={<MapIcon size={16} color={theme.text} />}
        onPress={toggle}>
        {isOpen ? t('hotels.hideMap') : t('hotels.showMap')}
      </Button>
      {inlineSrc ? <HotelsMapFrame src={inlineSrc} /> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  block: {
    marginTop: Spacing.one,
  },
  toggle: {
    alignSelf: 'flex-start',
    paddingHorizontal: 0,
  },
});
